// §9.2 Тээвэр (Shipping / D11) tab. Controlled render of detail.returnRequest;
// each action dialog holds its own local state and calls onChange() (detail
// mutate) on success. Mutations send an Idempotency-Key via ReturnApi; errors
// surface through the component (ReturnApi's HttpRequest has no global toaster).
import {
  Badge, Button, Group, Modal, Paper, Progress, Select, Stack, Text, Textarea, TextInput, ThemeIcon, Timeline, Title
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { DisabledReason, HelpButtonGroup, HelpIcon, i18n, showNotification } from "@dehub/theme";
import type { ReactElement } from "react";
import { CheckCircleIcon, TruckIcon, PackageIcon, MapPinIcon, WarningOctagonIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ReturnApi } from "../../apis";
import { usePermission } from "../permission";
import { IAuth } from "../../store/Auth";
import { dateTimeSecFormat } from "../../utils/date";
import { DeliveryActionButton } from "../delivery/action-controls";
import { useReturnActions } from "./return-actions";
import type { IReturnDetail, ShippingResponsibility, TransitExceptionType } from "../../apis/return";

type TFn = (k: string, o?: Record<string, unknown>) => string;

const exceptionTypeOptions = (t: TFn) => [
  { value: "LOST",               label: t("transitResolve.exceptionType.LOST") },
  { value: "DAMAGED_IN_TRANSIT", label: t("transitResolve.exceptionType.DAMAGED_IN_TRANSIT") },
  { value: "WRONG_DESTINATION",  label: t("transitResolve.exceptionType.WRONG_DESTINATION") },
  { value: "STOLEN",             label: t("transitResolve.exceptionType.STOLEN") }
];

const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

const shippingLabel = (v?: ShippingResponsibility | null) => (v ? i18n.t(`returns:shipping.responsibility.${v}`) : "-");

// Humanised current shipping state by status.
const stateLabel = (v: string) => i18n.t(`returns:shippingPanel.state.${v}`, { defaultValue: v });

const fmt = (s?: string | null) => dateTimeSecFormat(s);

function notifyOk (msg: string){ showNotification({ color: "green", title: i18n.t("returns:toast.success"), message: msg }); }
function notifyErr (e: unknown){ showNotification({ color: "red", title: i18n.t("returns:toast.error"), message: (e as Error).message }); }

interface Props {
  detail: IReturnDetail;
  onChange: () => void;
}

export function ReturnShippingPanel ({ detail, onChange }: Props){
  const { t } = useTranslation("returns");
  const EXCEPTION_TYPE_OPTIONS = exceptionTypeOptions(t);
  const id = detail.id;
  const ship = detail.shippingResponsibility;
  const status = detail.status;

  const [pickupOpen, pickupH] = useDisclosure(false);
  const [shipOpen, shipH]     = useDisclosure(false);
  const [trackOpen, trackH]   = useDisclosure(false);
  const [reportOpen, reportH] = useDisclosure(false);
  const [arrivedOpen, arrivedH] = useDisclosure(false);
  const [arrivedNote, setArrivedNote] = useState("");
  const [resolveOpen, resolveH] = useDisclosure(false);
  const [resolveNote, setResolveNote] = useState("");
  const [busy, setBusy] = useState(false);

  // "Goods arrived" - seller warehouse staff only (same permission as the receipt).
  const myBusinessId = useSelector((state: { auth: IAuth }) => state.auth.user?.currentBusinessId ?? null);
  const { hasAccess: canReceive } = usePermission("RETURN_WAREHOUSE_RECEIVE");

  // Buttons stay visible without the permission, aria-disabled with the code (return-actions matrix).
  const { stateOf, permissionState } = useReturnActions();
  const asBtn = (s: { enabled: boolean; reason: string | null }) => ({ allowed: s.enabled, reason: s.reason });
  // @dehub/help: attached «?» (R2) + reason popover while unavailable (R4), `order.returns-detail.{action|reason}.{id}`.
  const withHelp = (id: string, st: { enabled: boolean; code: string | null }, node: ReactElement) => (
    <DisabledReason helpKey={`order.returns-detail.reason.${id}`} reason={st.code} when={!st.enabled} position="bottom-start">
      <HelpButtonGroup helpKey={`order.returns-detail.action.${id}`}>{node}</HelpButtonGroup>
    </DisabledReason>
  );
  const [excType, setExcType] = useState<string | null>(null);
  const [excCarrierRef, setExcCarrierRef] = useState("");
  const [excDescription, setExcDescription] = useState("");

  const [pickupDate, setPickupDate] = useState<string | null>(null);
  const [pickupNotes, setPickupNotes] = useState("");
  const [carrier, setCarrier]   = useState(detail.pickupCarrier ?? "");
  const [tracking, setTracking] = useState(detail.pickupTrackingNumber ?? "");

  const run = (fn: () => Promise<unknown>, okMsg: string, close: () => void) => async () => {
    setBusy(true);
    try { await fn(); notifyOk(okMsg); close(); onChange(); }
    catch (e) { notifyErr(e); }
    finally { setBusy(false); }
  };

  // Consignment return parked in AWAITING_RESOLUTION - the seller completes it by hand
  // (no automatic sales-report confirmation exists). Rendered in every layout of the panel.
  const canResolveConsignment = status === "AWAITING_RESOLUTION" && !!detail.isConsignmentReturn
    && canReceive && !!myBusinessId && detail.sellerBusinessId === myBusinessId;
  const resolveBlock = canResolveConsignment ? (
    <>
      <Group>
        <Button leftSection={<CheckCircleIcon size={18} />} onClick={resolveH.open}>{t("shippingPanel.resolveConsignment")}</Button>
      </Group>
      <Modal opened={resolveOpen} onClose={resolveH.close} title={t("shippingPanel.resolveConsignment")} size={440}>
        <Stack gap="md">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon color="green" variant="light" size="lg" radius="xl"><CheckCircleIcon size={20} /></ThemeIcon>
            <Text size="sm">{t("shippingPanel.resolveConsignmentConfirm")}</Text>
          </Group>
          <Paper withBorder radius="md" p="sm">
            <Group justify="space-between" gap="xs" wrap="nowrap">
              <Text size="sm" c="dimmed">{t("shippingPanel.returnNumber")}</Text>
              <Text size="sm" fw={500} ff="monospace">{detail.returnNumber}</Text>
            </Group>
          </Paper>
          <Textarea
            label={t("shippingPanel.note")}
            value={resolveNote}
            onChange={(e) => setResolveNote(e.currentTarget.value)}
            maxLength={500}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={resolveH.close}>{t("resolutionPanel.cancel")}</Button>
            <Button
              color="green"
              loading={busy}
              onClick={run(
                () => ReturnApi.resolveConsignment(id, { note: resolveNote.trim() || null, expectedVersion: detail.version }),
                t("shippingPanel.toast.consignmentResolved"),
                resolveH.close
              )}
            >
              {t("shippingPanel.resolveConsignment")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  ) : null;

  // ── Empty states ──
  if (ship === "NOT_REQUIRED") {
    return <Stack gap="md"><Text c="dimmed">{t("shippingPanel.notRequired")}</Text>{resolveBlock}</Stack>;
  }
  if (!ship) {
    return <Stack gap="md"><Text c="dimmed">{t("shippingPanel.noShippingInfo")}</Text>{resolveBlock}</Stack>;
  }

  // ── SLA bar (pickup-relevant statuses) ──
  const slaTotal = detail.pickupSlaDays ?? 14;
  const remaining = detail.slaDaysRemaining;
  const overdue = remaining != null && remaining <= 0;
  const usedDays = remaining != null ? Math.min(slaTotal, Math.max(0, slaTotal - remaining)) : 0;
  const pickupStates = ["AWAITING_PICKUP_SCHEDULE", "AWAITING_SHIPMENT_INFO", "IN_TRANSIT"];
  const showSla = detail.slaDeadline != null && pickupStates.includes(status);

  // ── Transit timeline active step ──
  const steps = [
    { label: t("shippingPanel.stepPickupScheduled"), ts: detail.pickupScheduledAt, icon: <MapPinIcon size={14} /> },
    { label: t("shippingPanel.stepInTransit"), ts: detail.inTransitSince, icon: <TruckIcon size={14} /> },
    { label: t("shippingPanel.stepWarehouseReceived"), ts: detail.warehouseReceivedAt, icon: <PackageIcon size={14} /> }
  ];
  const activeStep = steps.filter((s) => s.ts).length;

  // ── Action availability (matches backend status preconditions) ──
  const canSchedule = status === "AWAITING_PICKUP_SCHEDULE";
  const canMarkShipped = status === "AWAITING_SHIPMENT_INFO";
  const canUpdateTracking = status === "IN_TRANSIT";
  const canReport = status === "IN_TRANSIT";
  // mark-arrived.ts: seller side, IN_TRANSIT, RETURN_WAREHOUSE_RECEIVE.
  const canMarkArrived = status === "IN_TRANSIT" && (!myBusinessId || detail.sellerBusinessId === myBusinessId);

  return (
    <Stack gap="md">
      {/* Channel + state */}
      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" wrap="wrap">
          <Stack gap={2}>
            <Text size="sm" c="dimmed">{t("transitResolve.shippingChannel")}</Text>
            <Text size="sm" fw={600}>{shippingLabel(ship)}</Text>
          </Stack>
          <Badge color="gray" variant="light" size="lg" radius="xl">
            {stateLabel(status)}
          </Badge>
        </Group>
      </Paper>

      {/* Pickup / transit info */}
      <Paper withBorder radius="md" p="md">
        <Title order={6} mb="xs">
          {t("shippingPanel.pickupInfoTitle")}
          <HelpIcon helpKey="order.returns-detail.section.shipping" variant="section" />
        </Title>
        <Stack gap="xs">
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">{t("shippingPanel.carrier")}</Text>
            <Text size="sm" fw={500}>{detail.pickupCarrier || "-"}</Text>
          </Group>
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">{t("shippingPanel.trackingNumber")}</Text>
            <Text size="sm" fw={500} ff="monospace" style={TNUM}>{detail.pickupTrackingNumber || "-"}</Text>
          </Group>
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">{t("shippingPanel.deliveryNote")}</Text>
            <Text size="sm" fw={500}>
              {detail.pickupDeliveryNoteId ? t("shippingPanel.deliveryNoteLinked") : "-"}
            </Text>
          </Group>
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">{t("transitResolve.pickupDate")}</Text>
            <Text size="sm" fw={500} style={TNUM}>{fmt(detail.pickupScheduledAt)}</Text>
          </Group>
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">{t("shippingPanel.inTransitSince")}</Text>
            <Text size="sm" fw={500} style={TNUM}>{fmt(detail.inTransitSince)}</Text>
          </Group>
        </Stack>

        {showSla ? (
          <Stack gap={4} mt="md">
            <Group justify="space-between" gap="xs">
              <Text size="sm" c="dimmed">{t("detail.field.pickupSla")}</Text>
              <Text size="sm" c={overdue ? "var(--mantine-color-error)" : "dimmed"} fw={overdue ? 600 : 400} style={TNUM}>
                {overdue
                  ? t("shippingPanel.daysOverdueShort", { count: Math.abs(remaining ?? 0) })
                  : t("shippingPanel.daysUsed", { used: usedDays, total: slaTotal })}
              </Text>
            </Group>
            <Progress value={Math.min(100, (usedDays / slaTotal) * 100)} color={overdue ? "red" : "gray.7"} radius="sm" />
          </Stack>
        ) : null}
      </Paper>

      {/* Transit timeline */}
      <Paper withBorder radius="md" p="md">
        <Title order={6} mb="sm">{t("shippingPanel.transitProgress")}</Title>
        <Timeline active={activeStep - 1} bulletSize={20} lineWidth={2}>
          {steps.map((s) => (
            <Timeline.Item key={s.label} bullet={s.icon} title={<Text size="sm">{s.label}</Text>}>
              <Text size="sm" c="dimmed" style={TNUM}>{fmt(s.ts)}</Text>
            </Timeline.Item>
          ))}
        </Timeline>
      </Paper>

      {/* Actions */}
      {(canSchedule || canMarkShipped || canUpdateTracking || canReport || canMarkArrived) ? (
        <Group gap="sm">
          {canSchedule ? (
            withHelp("schedule-pickup", stateOf("schedulePickup", detail),
              <DeliveryActionButton data-help-id="return-schedule-pickup" state={asBtn(stateOf("schedulePickup", detail))} leftSection={<TruckIcon size={18} />} onClick={pickupH.open}>
                {t("shippingPanel.schedulePickup")}
              </DeliveryActionButton>)
          ) : null}
          {canMarkShipped ? (
            withHelp("mark-shipped", stateOf("markShipped", detail),
              <DeliveryActionButton state={asBtn(stateOf("markShipped", detail))} leftSection={<PackageIcon size={18} />} onClick={shipH.open}>
                {t("shippingPanel.markShipped")}
              </DeliveryActionButton>)
          ) : null}
          {canUpdateTracking ? (
            <Button variant="default" leftSection={<MapPinIcon size={18} />} onClick={trackH.open}>{t("shippingPanel.updateTracking")}</Button>
          ) : null}
          {canMarkArrived ? (
            withHelp("mark-arrived", permissionState("warehouseReceive"),
              <DeliveryActionButton data-help-id="return-mark-arrived" state={asBtn(permissionState("warehouseReceive"))} variant="default" leftSection={<CheckCircleIcon size={18} />} onClick={arrivedH.open}>
                {t("shippingPanel.markArrived")}
              </DeliveryActionButton>)
          ) : null}
          {canReport ? (
            withHelp("report-transit", stateOf("reportTransit", detail),
              <DeliveryActionButton state={asBtn(stateOf("reportTransit", detail))} variant="default" leftSection={<WarningOctagonIcon size={18} />} onClick={reportH.open}>
                {t("shippingPanel.reportException")}
              </DeliveryActionButton>)
          ) : null}
        </Group>
      ) : null}

      {/* Schedule pickup dialog (the §9.1 "Pickup төлөвлөх" target) */}
      <Modal opened={pickupOpen} onClose={pickupH.close} title={t("shippingPanel.schedulePickup")} size={440}>
        <Stack gap="md">
          <DateTimePicker
            label={t("shippingPanel.pickupDateOptional")}
            placeholder={t("shippingPanel.pickupDatePlaceholder")}
            value={pickupDate}
            onChange={(v) => setPickupDate(v as string | null)}
            clearable
          />
          <Textarea
            label={t("shippingPanel.note")}
            value={pickupNotes}
            onChange={(e) => setPickupNotes(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={pickupH.close}>{t("resolutionPanel.cancel")}</Button>
            <Button
              loading={busy}
              onClick={run(
                () => ReturnApi.schedulePickup(id, {
                  pickupScheduledAt: pickupDate ? new Date(pickupDate).toISOString() : undefined,
                  notes: pickupNotes || null,
                  expectedVersion: detail.version
                }),
                t("shippingPanel.toast.pickupScheduled"),
                pickupH.close
              )}
            >
              {t("shippingPanel.schedule")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {resolveBlock}

      {/* Goods arrived dialog */}
      <Modal opened={arrivedOpen} onClose={arrivedH.close} title={t("shippingPanel.markArrived")} size={440}>
        <Stack gap="md">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon color="green" variant="light" size="lg" radius="xl"><CheckCircleIcon size={20} /></ThemeIcon>
            <Text size="sm">{t("shippingPanel.arrivedConfirm")}</Text>
          </Group>
          <Paper withBorder radius="md" p="sm">
            <Stack gap="xs">
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed">{t("shippingPanel.returnNumber")}</Text>
                <Text size="sm" fw={500} ff="monospace">{detail.returnNumber}</Text>
              </Group>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed">{t("shippingPanel.carrier")}</Text>
                <Text size="sm" fw={500}>{detail.pickupCarrier || "-"}</Text>
              </Group>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed">{t("shippingPanel.trackingNumber")}</Text>
                <Text size="sm" fw={500} ff="monospace" style={TNUM}>{detail.pickupTrackingNumber ?? "-"}</Text>
              </Group>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed">{t("shippingPanel.inTransitSince")}</Text>
                <Text size="sm" fw={500} style={TNUM}>{fmt(detail.inTransitSince)}</Text>
              </Group>
            </Stack>
          </Paper>
          <Textarea
            label={t("shippingPanel.note")}
            value={arrivedNote}
            onChange={(e) => setArrivedNote(e.currentTarget.value)}
            maxLength={500}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={arrivedH.close}>{t("resolutionPanel.cancel")}</Button>
            <Button
              color="green"
              loading={busy}
              onClick={run(
                () => ReturnApi.markArrived(id, { note: arrivedNote.trim() || null, expectedVersion: detail.version }),
                t("shippingPanel.toast.arrived"),
                arrivedH.close
              )}
            >
              {t("shippingPanel.markArrived")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Mark shipped dialog */}
      <Modal opened={shipOpen} onClose={shipH.close} title={t("shippingPanel.markShipped")} size={440}>
        <Stack gap="md">
          <TextInput
            label={t("shippingPanel.carrier")}
            placeholder="DHL"
            required
            value={carrier}
            onChange={(e) => setCarrier(e.currentTarget.value)}
            maxLength={40}
          />
          <TextInput
            label={t("shippingPanel.trackingNumber")}
            placeholder="JD0014234234"
            required
            value={tracking}
            onChange={(e) => setTracking(e.currentTarget.value)}
            maxLength={128}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={shipH.close}>{t("resolutionPanel.cancel")}</Button>
            <Button
              loading={busy}
              disabled={!carrier.trim() || !tracking.trim()}
              onClick={run(
                () => ReturnApi.markShipped(id, { carrier: carrier.trim(), trackingNumber: tracking.trim(), expectedVersion: detail.version }),
                t("shippingPanel.toast.marked"),
                shipH.close
              )}
            >
              {t("shippingPanel.mark")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Report transit exception dialog (§D13) */}
      <Modal opened={reportOpen} onClose={reportH.close} title={t("shippingPanel.reportException")} size={440}>
        <Stack gap="md">
          <Select
            label={t("shippingPanel.exceptionTypeLabel")}
            placeholder={t("shippingPanel.select")}
            required
            data={EXCEPTION_TYPE_OPTIONS}
            value={excType}
            onChange={setExcType}
          />
          <TextInput
            label={t("shippingPanel.carrierRefOptional")}
            placeholder="DHL-EXC-2026-..."
            value={excCarrierRef}
            onChange={(e) => setExcCarrierRef(e.currentTarget.value)}
            maxLength={128}
          />
          <Textarea
            label={t("shippingPanel.descriptionOptional")}
            value={excDescription}
            onChange={(e) => setExcDescription(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Text size="sm" c="dimmed">
            {t("shippingPanel.reportHint")}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={reportH.close}>{t("resolutionPanel.cancel")}</Button>
            <Button
              loading={busy}
              disabled={!excType}
              onClick={run(
                () => ReturnApi.reportTransitException(id, {
                  exceptionType  : excType as TransitExceptionType,
                  reportedBy     : "MANAGER",
                  carrierRef     : excCarrierRef.trim() || null,
                  description    : excDescription.trim() || null,
                  expectedVersion: detail.version
                }),
                t("shippingPanel.toast.exceptionReported"),
                reportH.close
              )}
            >
              {t("shippingPanel.mark")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Update tracking dialog */}
      <Modal opened={trackOpen} onClose={trackH.close} title={t("shippingPanel.updateTracking")} size={440}>
        <Stack gap="md">
          <TextInput
            label={t("shippingPanel.carrier")}
            placeholder="DHL"
            value={carrier}
            onChange={(e) => setCarrier(e.currentTarget.value)}
            maxLength={40}
          />
          <TextInput
            label={t("shippingPanel.trackingNumber")}
            placeholder="JD0014234234"
            required
            value={tracking}
            onChange={(e) => setTracking(e.currentTarget.value)}
            maxLength={128}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={trackH.close}>{t("resolutionPanel.cancel")}</Button>
            <Button
              loading={busy}
              disabled={!tracking.trim()}
              onClick={run(
                () => ReturnApi.updateTracking(id, { carrier: carrier.trim() || null, trackingNumber: tracking.trim(), expectedVersion: detail.version }),
                t("shippingPanel.toast.trackingUpdated"),
                trackH.close
              )}
            >
              {t("shippingPanel.update")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
